import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Telescope, Mail, Lock, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export default function Login() {
  const { login, user, loading } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email, password);
      nav("/", { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Identifiants invalides");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen relative grid lg:grid-cols-2">
      <div className="starfield" aria-hidden />
      <div className="hidden lg:flex flex-col justify-between p-12 relative overflow-hidden border-r border-white/5">
        <div className="absolute -top-32 -left-20 w-[36rem] h-[36rem] aurora rounded-full" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-9 h-9 rounded-md border border-white/10 grid place-items-center bg-gradient-to-br from-violet-500/40 to-blue-500/10">
            <Telescope className="w-4 h-4 text-violet-200" strokeWidth={1.5} />
          </div>
          <div className="font-medium tracking-tight">AstroLift</div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="label-eyebrow mb-4">Observatoire numérique</div>
          <h1 className="text-5xl font-light tracking-tighter leading-[1.05]">
            Révélez le potentiel <span className="text-violet-300">réel</span> de vos astrophotos.
          </h1>
          <p className="mt-6 text-muted-foreground leading-relaxed">
            AstroLift n'invente rien. Le moteur exploite uniquement le signal réellement capté
            par votre instrument — Vespera, iPhone, APN, télescope, caméra astro — pour en
            extraire jusqu'à 95 % du potentiel scientifique.
          </p>
        </div>
        <div className="relative z-10 grid grid-cols-3 gap-4 max-w-md">
          {["Vespera 2", "iPhone", "APN", "Télescope", "Caméra astro", "FITS / XISF"].map((t) => (
            <div key={t} className="px-3 py-2 border border-white/10 rounded-md text-xs text-center text-muted-foreground">{t}</div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-center p-8 relative">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex items-center gap-3">
            <div className="w-9 h-9 rounded-md border border-white/10 grid place-items-center bg-gradient-to-br from-violet-500/30 to-blue-500/10">
              <Telescope className="w-4 h-4 text-violet-200" strokeWidth={1.5} />
            </div>
            <div className="font-medium tracking-tight">AstroLift</div>
          </div>
          <div className="label-eyebrow mb-3">Connexion sécurisée</div>
          <h2 className="text-3xl font-light tracking-tight mb-2">Bienvenue dans AstroLift</h2>
          <p className="text-sm text-muted-foreground mb-8">Révélez automatiquement le potentiel réel de vos astrophotos.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="label-eyebrow block mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="w-full bg-secondary/40 border border-border rounded-md py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  placeholder="vous@exemple.com"
                />
              </div>
            </div>
            <div>
              <label className="label-eyebrow block mb-2">Mot de passe</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="w-full bg-secondary/40 border border-border rounded-md py-2.5 pl-10 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors"
                  placeholder="••••••••"
                />
              </div>
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-btn"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-md py-2.5 text-sm font-medium hover:bg-primary/90 transition-all glow-violet disabled:opacity-60"
            >
              {busy ? "Connexion…" : "Accéder à AstroLift"} {!busy && <ArrowRight className="w-4 h-4" />}
            </button>
          </form>

          <p className="mt-10 text-[11px] text-muted-foreground/70 tracking-wide">
            Authentification sécurisée par JWT · Hash bcrypt · Variables d'environnement
          </p>
          <div className="mt-6 text-center">
            <span className="text-sm text-muted-foreground">Pas encore de compte ? </span>
            <a href="/signup" className="text-sm text-violet-300 hover:underline" data-testid="link-to-signup">Créer un compte</a>
          </div>
        </div>
        <Toaster theme="dark" />
      </div>
    </div>
  );
}
